import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Chip,
  Tooltip
} from '@mui/material';
import { Add, Edit, Delete, Refresh } from '@mui/icons-material';
import { slaAPI } from '../../api/sla.api';

const SlaConfig = () => {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    ticket_type: 'incidente',
    priority: 'media',
    response_hours: 2,
    resolution_hours: 24,
    description: '',
    is_active: true
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadConfigs();
  }, []);

  const loadConfigs = async () => {
    setLoading(true);
    try {
      const data = await slaAPI.getAll();
      setConfigs(data);
    } catch (error) {
      console.error('Error loading SLA configs:', error);
      setError('Error al cargar configuraciones SLA');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (config = null) => {
    if (config) {
      setEditingId(config.id);
      setFormData({
        ticket_type: config.ticket_type,
        priority: config.priority,
        response_hours: config.response_hours,
        resolution_hours: config.resolution_hours,
        description: config.description || '',
        is_active: config.is_active
      });
    } else {
      setEditingId(null);
      setFormData({
        ticket_type: 'incidente',
        priority: 'media',
        response_hours: 2,
        resolution_hours: 24,
        description: '',
        is_active: true
      });
    }
    setDialogOpen(true);
    setError('');
    setSuccess('');
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingId(null);
    setError('');
    setSuccess('');
  };

  const handleSubmit = async () => {
    try {
      if (editingId) {
        await slaAPI.update(editingId, formData);
        setSuccess('Configuración actualizada correctamente');
      } else {
        await slaAPI.create(formData);
        setSuccess('Configuración creada correctamente');
      }
      handleCloseDialog();
      loadConfigs();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al guardar la configuración');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Eliminar esta configuración SLA?')) {
      try {
        await slaAPI.delete(id);
        setSuccess('Configuración eliminada correctamente');
        loadConfigs();
      } catch (err) {
        setError('Error al eliminar la configuración');
      }
    }
  };

  const handleInitialize = async () => {
    if (window.confirm('¿Inicializar configuraciones por defecto? Esto agregará las configuraciones que no existan.')) {
      try {
        await slaAPI.initialize();
        setSuccess('Configuraciones por defecto inicializadas');
        loadConfigs();
      } catch (err) {
        setError('Error al inicializar configuraciones');
      }
    }
  };

  const getTicketTypeLabel = (type) => {
    const labels = {
      incidente: 'Incidente',
      peticion: 'Petición',
      instalacion: 'Instalación',
      retiro: 'Retiro',
      cambio_equipo: 'Cambio Equipo'
    };
    return labels[type] || type;
  };

  const getPriorityLabel = (priority) => {
    const labels = {
      baja: 'Baja',
      media: 'Media',
      alta: 'Alta',
      critica: 'Crítica'
    };
    return labels[priority] || priority;
  };

  const getPriorityColor = (priority) => {
    const colors = {
      baja: 'success',
      media: 'info',
      alta: 'warning',
      critica: 'error'
    };
    return colors[priority] || 'default';
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Configuración SLA</Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={loadConfigs}
            sx={{ mr: 1 }}
          >
            Actualizar
          </Button>
          <Button
            variant="outlined"
            onClick={handleInitialize}
            sx={{ mr: 1 }}
          >
            Inicializar Defecto
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
          >
            Nueva Configuración
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Card>
        <CardContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell>Tipo de Ticket</TableCell>
                  <TableCell>Prioridad</TableCell>
                  <TableCell>Respuesta (horas)</TableCell>
                  <TableCell>Resolución (horas)</TableCell>
                  <TableCell>Descripción</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell align="center">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : configs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                      <Typography variant="body1">No hay configuraciones SLA</Typography>
                      <Button
                        variant="outlined"
                        onClick={handleInitialize}
                        sx={{ mt: 2 }}
                      >
                        Inicializar Configuraciones por Defecto
                      </Button>
                    </TableCell>
                  </TableRow>
                ) : (
                  configs.map((config) => (
                    <TableRow key={config.id} hover>
                      <TableCell>
                        <Chip
                          label={getTicketTypeLabel(config.ticket_type)}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getPriorityLabel(config.priority)}
                          color={getPriorityColor(config.priority)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{config.response_hours}h</TableCell>
                      <TableCell>{config.resolution_hours}h</TableCell>
                      <TableCell>
                        <Tooltip title={config.description || ''}>
                          <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {config.description || '-'}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={config.is_active ? 'Activo' : 'Inactivo'}
                          color={config.is_active ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => handleOpenDialog(config)}>
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton size="small" color="error" onClick={() => handleDelete(config.id)}>
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingId ? 'Editar Configuración SLA' : 'Nueva Configuración SLA'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Tipo de Ticket</InputLabel>
              <Select
                value={formData.ticket_type}
                onChange={(e) => setFormData({ ...formData, ticket_type: e.target.value })}
                label="Tipo de Ticket"
              >
                <MenuItem value="incidente">Incidente</MenuItem>
                <MenuItem value="peticion">Petición</MenuItem>
                <MenuItem value="instalacion">Instalación</MenuItem>
                <MenuItem value="retiro">Retiro</MenuItem>
                <MenuItem value="cambio_equipo">Cambio de Equipo</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Prioridad</InputLabel>
              <Select
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                label="Prioridad"
              >
                <MenuItem value="baja">Baja</MenuItem>
                <MenuItem value="media">Media</MenuItem>
                <MenuItem value="alta">Alta</MenuItem>
                <MenuItem value="critica">Crítica</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              type="number"
              label="Horas para respuesta"
              value={formData.response_hours}
              onChange={(e) => setFormData({ ...formData, response_hours: parseFloat(e.target.value) })}
              sx={{ mb: 2 }}
              inputProps={{ min: 0.5, max: 168, step: 0.5 }}
            />

            <TextField
              fullWidth
              type="number"
              label="Horas para resolución"
              value={formData.resolution_hours}
              onChange={(e) => setFormData({ ...formData, resolution_hours: parseFloat(e.target.value) })}
              sx={{ mb: 2 }}
              inputProps={{ min: 1, max: 720, step: 1 }}
            />

            <TextField
              fullWidth
              label="Descripción"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              multiline
              rows={2}
              sx={{ mb: 2 }}
              placeholder="Descripción opcional de esta configuración"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
              }
              label="Activo"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingId ? 'Actualizar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SlaConfig;
